/** Чистые функции ленты дней Афиши. */

/** Завершилось строго по окончанию, а без него — по началу; без суток запаса. */
export function isPastEvent(
  event: { event_date: string | null; event_end_date?: string | null },
  now: Date = new Date(),
): boolean {
  const end = event.event_end_date ?? event.event_date;
  return !!end && new Date(end).getTime() < now.getTime();
}

export function keyOfDate(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Ключи дней, под которыми нужна точка: есть хотя бы одно событие (многодневное — каждый его день). */
export function eventDotKeys(
  events: { event_date: string | null; event_end_date?: string | null }[],
): Set<string> {
  const out = new Set<string>();
  for (const e of events) {
    if (!e.event_date) continue;
    const start = new Date(e.event_date);
    const end = e.event_end_date ? new Date(e.event_end_date) : start;
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    let guard = 0;
    while (d <= last && guard++ < 62) {
      out.add(keyOfDate(d));
      d.setDate(d.getDate() + 1);
    }
  }
  return out;
}

/** Ширина ячейки, чтобы ровно `visible` дней помещались в ширину ленты. */
export function dayCellWidth(containerWidth: number, visible = 7, gap = 4): number {
  return Math.max(0, Math.floor((containerWidth - gap * (visible - 1)) / visible));
}

const IST_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Istanbul",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** Дата дня в Стамбуле: «YYYY-MM-DD». */
export function istanbulDay(iso: string | Date): string {
  return IST_DAY.format(typeof iso === "string" ? new Date(iso) : iso);
}

export interface EventDaySlot {
  /** Полдень этого дня по Стамбулу (UTC+3), для группировки. */
  iso: string;
  dayIndex: number;
  dayCount: number;
}

/**
 * Дни, под которыми показывать карточку многодневного события (2–7 дней по Стамбулу):
 * от старта (или сегодня, если уже идёт) до дня окончания включительно.
 * Иначе null — событие группируется по-старому.
 */
export function multiDaySlots(
  start: string | null,
  end: string | null | undefined,
  now: Date = new Date(),
): EventDaySlot[] | null {
  if (!start || !end) return null;
  const s = istanbulDay(start);
  const e = istanbulDay(end);
  const toUtc = (d: string) => Date.parse(`${d}T00:00:00Z`);
  const dayCount = Math.round((toUtc(e) - toUtc(s)) / 86_400_000) + 1;
  if (dayCount < 2 || dayCount > 7) return null;
  const today = istanbulDay(now);
  const out: EventDaySlot[] = [];
  for (let i = 0; i < dayCount; i++) {
    const d = new Date(toUtc(s) + i * 86_400_000).toISOString().slice(0, 10);
    if (d < today) continue;
    out.push({ iso: `${d}T12:00:00+03:00`, dayIndex: i + 1, dayCount });
  }
  return out;
}

const MONTHS_GEN = ["января","февраля","марта","апреля","мая","июня","июля","августа","сентября","октября","ноября","декабря"];

/** Пометка на карточке многодневного события в дневной группе. */
export function multiDayNote(dayIndex: number, dayCount: number, end: string): string {
  if (dayIndex === 1) {
    const [, m, d] = istanbulDay(end).split("-").map(Number);
    return `Идёт до ${d} ${MONTHS_GEN[m - 1]}`;
  }
  return `День ${dayIndex} из ${dayCount}`;
}
